import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '@/components/Header';
import { PaperPlaneRight, Sparkle, User } from '@phosphor-icons/react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

function AIAssistant({ user }) {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! I'm Helm, your professional workflow assistant. I can help you organize tasks, track deadlines, manage compliance filings, and answer questions about your workload. How can I help you today?"
    }
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');
    setMessages([...messages, { role: 'user', content: userMessage }]);
    setSending(true);

    try {
      const response = await axios.post(
        `${API}/ai/chat`,
        { message: userMessage },
        { withCredentials: true }
      );
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: response.data.response }
      ]);
    } catch (error) {
      console.error('AI chat error:', error);
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: "I'm sorry, I encountered an error. Please try again." }
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header user={user} />
      
      <main className="flex-1 max-w-4xl mx-auto w-full p-6 md:p-8 flex flex-col">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-slate-900 mb-2 flex items-center gap-3">
            <Sparkle size={32} weight="duotone" className="text-navy" />
            AI Assistant
          </h1>
          <p className="text-sm md:text-base text-slate-500">
            Ask me anything about organizing your tasks, documents, and admin work
          </p>
        </div>

        {/* Chat Container */}
        <div className="flex-1 bg-white border border-slate-200 rounded-lg shadow-sm flex flex-col overflow-hidden">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
            {messages.map((msg, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${idx}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-lg bg-navy flex items-center justify-center flex-shrink-0">
                    <Sparkle size={16} weight="fill" className="text-white" />
                  </div>
                )}
                
                <div className={`max-w-[80%] rounded-lg px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-navy text-white'
                    : 'bg-slate-50 text-slate-900 border border-slate-200'
                }`}>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                </div>

                {msg.role === 'user' && (
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-lg" />
                    ) : (
                      <User size={16} className="text-slate-500" />
                    )}
                  </div>
                )}
              </motion.div>
            ))}

            {sending && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex gap-3 justify-start"
                data-testid="typing-indicator"
              >
                <div className="w-8 h-8 rounded-lg bg-navy flex items-center justify-center flex-shrink-0">
                  <Sparkle size={16} weight="fill" className="text-white" />
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </motion.div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Form */}
          <form onSubmit={sendMessage} className="border-t border-slate-200 p-4">
            <div className="flex gap-3">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me anything... e.g., 'What deadlines do I have for MTN Nigeria this week?'"
                disabled={sending}
                className="flex-1 bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-3 text-slate-900 disabled:opacity-50"
                data-testid="ai-input"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                className="bg-navy text-white hover:bg-navy-hover transition-all duration-200 shadow-sm font-medium px-4 py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="send-message-btn"
              >
                <PaperPlaneRight size={20} weight="fill" />
                <span className="hidden sm:inline">Send</span>
              </button>
            </div>
          </form>
        </div>

        {/* Suggestions */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            "What tasks do I have due this week?",
            "What regulatory filings are coming up?",
            "Help me organize my tasks by priority",
            "What do I need to file my CAC annual return?"
          ].map((suggestion, i) => (
            <button
              key={i}
              onClick={() => setInput(suggestion)}
              className="bg-white border border-slate-200 hover:bg-slate-50 hover:shadow-sm hover:-translate-y-[1px] transition-all duration-200 rounded-lg p-3 text-left"
              data-testid={`suggestion-${i + 1}`}
            >
              <p className="text-sm font-medium text-slate-700">{suggestion}</p>
            </button>
          ))}
        </div>

        {/* Tip */}
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start gap-2">
          <Sparkle size={16} weight="fill" className="text-navy flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600">
            <span className="font-medium">Tip:</span> Go to <a href="/tasks" className="underline text-navy">Tasks</a> and use "Parse with AI" to create tasks from plain language.
          </p>
        </div>
      </main>
    </div>
  );
}

export default AIAssistant;
